import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { useUserLibraryInfinite } from '../hooks/useUserLibrary';
import BookCard from '../components/BookCard';
import BookCardList from '../components/BookCardList';
import ViewControls from '../components/ViewControls';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';

type TabType = 'reading' | 'read' | 'downloaded';

const MyBooksPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('reading');
  const [direction, setDirection] = useState(0);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'newest' | 'popular'>('newest');

  const tabToLibraryTab = (tab: TabType) => {
    switch (tab) {
      case 'reading': return 'in-progress' as const;
      case 'read': return 'completed' as const;
      case 'downloaded': return 'saved' as const;
    }
  };

  const {
    items: rawBooks,
    isLoading: currentLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useUserLibraryInfinite(tabToLibraryTab(activeTab), { limit: 10 });

  const { loadMoreRef } = useInfiniteScroll({
    hasMore: hasNextPage,
    isLoading: isFetchingNextPage,
    onLoadMore: fetchNextPage,
  });

  const tabs = [
    { id: 'reading' as TabType, label: 'در حال خواندن' },
    { id: 'read' as TabType, label: 'خوانده شده' },
    { id: 'downloaded' as TabType, label: 'ذخیره شده' },
  ];

  const handleTabChange = (newTab: TabType) => {
    const currentIndex = tabs.findIndex(t => t.id === activeTab);
    const newIndex = tabs.findIndex(t => t.id === newTab);
    setDirection(newIndex > currentIndex ? 1 : -1);
    setActiveTab(newTab);
  };

  const currentBooks = useMemo(() => {
    const sorted = [...rawBooks];
    if (sortBy === 'newest') {
      sorted.sort((a, b) => {
        const dateA = new Date(a.book?.created_at || 0).getTime();
        const dateB = new Date(b.book?.created_at || 0).getTime();
        return dateB - dateA;
      });
    } else if (sortBy === 'popular') {
      sorted.sort((a, b) => {
        const ratingA = a.book?.average_rating || a.book?.rating || 0;
        const ratingB = b.book?.average_rating || b.book?.rating || 0;
        return ratingB - ratingA;
      });
    }
    return sorted;
  }, [rawBooks, sortBy]);

  const carouselBooks = useMemo(
    () =>
      currentBooks.map((item) => ({
        book_id: item.book?.book_id,
        title: item.book?.title,
        author: item.book?.author,
        cover_image_url: item.book?.cover_image_url ?? null,
        topic: item.book?.topic,
      })),
    [currentBooks]
  );

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction > 0 ? -100 : 100,
      opacity: 0,
    }),
  };

  return (
    <div className="min-h-screen bg-white" dir="rtl">
      <header className="sticky top-0 z-40 px-4 py-3 bg-white border-b border-gray-100 pt-safe-top">
        <div className="flex items-center justify-between mt-2">
          <button
            onClick={() => navigate('/library')}
            className="p-2 hover:bg-gray-100 rounded-2xl transition-colors"
            aria-label="بازگشت"
          >
            <ChevronRight className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="font-persian font-bold text-base text-gray-900">
            کتاب‌های من
          </h1>
          <div className="w-9" />
        </div>
      </header>

      <div className="px-4 pt-4 pb-3 bg-white">
        <div className="relative flex items-center bg-gray-200 rounded-xl">
          <motion.div
            className="absolute rounded-xl shadow-md pointer-events-none z-0"
            style={{ backgroundColor: '#6e61ff', top: 0, bottom: 0 }}
            animate={{
              width: `${100 / tabs.length}%`,
              right: `${tabs.findIndex(t => t.id === activeTab) * (100 / tabs.length)}%`,
            }}
            transition={{ type: 'spring', stiffness: 400, damping: 35 }}
          />
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={cn(
                'relative z-10 flex-1 flex items-center justify-center font-persian font-bold text-sm pt-3 pb-2 rounded-xl transition-colors duration-200',
                activeTab === tab.id ? 'text-white' : 'text-gray-500'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 pt-2">
        <ViewControls
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          showSort={false}
          sortBy={sortBy}
          onSortChange={setSortBy}
          className="mb-4"
        />
      </div>

      <div className="px-4 pt-2 pb-28 overflow-hidden">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={activeTab}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 350, damping: 35 },
              opacity: { duration: 0.25 },
            }}
          >
            {currentLoading ? (
              <div className="grid grid-cols-3 gap-x-6 gap-y-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="aspect-[2/3] bg-gray-200 rounded-2xl animate-pulse" />
                ))}
              </div>
            ) : currentBooks.length === 0 ? (
              <div className="flex items-center justify-center py-20">
                <p className="text-gray-500 text-base font-persian">هنوز کتابی وجود ندارد</p>
              </div>
            ) : (
              <>
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-3 gap-x-6 gap-y-6">
                    {currentBooks.map((item, index) => (
                      <div key={item.id}>
                        <BookCard
                          book={item.book}
                          progress={Number(item.progress ?? 0)}
                          showProgressBelow={activeTab === 'reading'}
                          isSaved={Boolean(item?.is_saved)}
                          onClick={() =>
                            navigate(`/library/book`, {
                              state: {
                                bookId: item.book?.book_id,
                                source: activeTab === 'reading' ? 'my-books-reading' : activeTab === 'read' ? 'my-books-read' : 'my-books-downloaded',
                                tab: activeTab,
                                books: carouselBooks,
                                initialIndex: index,
                              },
                            })
                          }
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {currentBooks.map((item, index) => (
                      <BookCardList
                        key={item.id}
                        book={item.book}
                        progress={Number(item.progress ?? 0)}
                        showProgressBelow={activeTab === 'reading'}
                        isSaved={Boolean(item?.is_saved)}
                        onClick={() =>
                          navigate(`/library/book`, {
                            state: {
                              bookId: item.book?.book_id,
                              source: activeTab === 'reading' ? 'my-books-reading' : activeTab === 'read' ? 'my-books-read' : 'my-books-downloaded',
                              tab: activeTab,
                              books: carouselBooks,
                              initialIndex: index,
                            },
                          })
                        }
                      />
                    ))}
                  </div>
                )}

                {/* Infinite scroll trigger */}
                <div ref={loadMoreRef} className="h-4" />
                
                {isFetchingNextPage && (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                  </div>
                )}
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default MyBooksPage;
